import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import {
  logsApi,
  StatsOverview,
  TimelineData,
  LogEntry,
} from "../services/logsApi";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import {
  AlertTriangle,
  Database,
  Server,
  TrendingUp,
  TrendingDown,
  CheckCircle2,
  Globe,
  Activity,
  ChevronLeft,
  ChevronRight,
  Calendar,
  Filter,
} from "lucide-react";
import CustomSelect from "./ui/CustomSelect";
import type { SelectOption } from "./ui/CustomSelect";
import {
  SkeletonCard,
  SkeletonChart,
  SkeletonTable,
  SkeletonDonut,
} from "./ui/Skeleton";

// Design tokens – purple theme matching layout
const t = {
  primary: "#7c3aed",
  primaryLight: "#ede9fe",
  primaryMid: "#c4b5fd",
  primaryPale: "#ddd6fe",
  surface: "#ffffff",
  border: "#e9e5f5",
  success: "#10b981",
  successBg: "#d1fae5",
  danger: "#ef4444",
  dangerBg: "#fee2e2",
  warn: "#f59e0b",
  warnBg: "#fef3c7",
  text: { primary: "#1e1b4b", secondary: "#6b7280", muted: "#9ca3b8" },
};

const DONUT_COLORS = ["#7c3aed", "#a78bfa", "#c4b5fd", "#ddd6fe", "#ede9fe"];

/* ─── Metric Card ─── */
interface MetricCardProps {
  label: string;
  value: string | number;
  icon: React.ReactNode;
  trend?: { value: string; positive: boolean };
}

function MetricCard({ label, value, icon, trend }: MetricCardProps) {
  return (
    <div
      className="rounded-2xl p-5"
      style={{ backgroundColor: t.surface, border: `1px solid ${t.border}` }}
    >
      <div className="flex items-start justify-between mb-3">
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center"
          style={{ backgroundColor: t.primaryLight }}
        >
          {icon}
        </div>
        {trend && (
          <div
            className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold"
            style={{
              backgroundColor: trend.positive ? t.successBg : t.dangerBg,
              color: trend.positive ? t.success : t.danger,
            }}
          >
            {trend.positive ? (
              <TrendingUp className="w-3 h-3" />
            ) : (
              <TrendingDown className="w-3 h-3" />
            )}
            {trend.value}
          </div>
        )}
      </div>
      <p
        className="text-[22px] font-bold leading-tight"
        style={{ color: t.text.primary }}
      >
        {value}
      </p>
      <p className="text-[12px] mt-1" style={{ color: t.text.secondary }}>
        {label}
      </p>
    </div>
  );
}

/* ─── Dashboard ─── */
export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState<StatsOverview["data"] | null>(null);
  const [timeline, setTimeline] = useState<TimelineData["data"]>([]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState("7d");
  const [chartYear, setChartYear] = useState(new Date().getFullYear());
  const [recentLogs, setRecentLogs] = useState<LogEntry[]>([]);
  const [logSearch, setLogSearch] = useState("");
  const [levelFilter, setLevelFilter] = useState("");
  const [serviceFilter, setServiceFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [ipFilter, setIpFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [dateFilterMode, setDateFilterMode] = useState<"range" | "month">(
    "range",
  );
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [showDateFilter, setShowDateFilter] = useState(false);

  // CustomSelect option arrays
  const timeRangeOptions: SelectOption[] = [
    { value: "1h", label: "1 heure" },
    { value: "6h", label: "6 heures" },
    { value: "24h", label: "24 heures" },
    { value: "7d", label: "7 jours" },
  ];

  const levelOptions: SelectOption[] = [
    { value: "", label: "Tous niveaux", dividerAfter: true },
    { value: "error", label: "Error" },
    { value: "warn", label: "Warn" },
    { value: "info", label: "Info" },
    { value: "debug", label: "Debug" },
  ];

  const serviceOptions: SelectOption[] = [
    { value: "", label: "Tous services", dividerAfter: true },
    ...(stats?.byService
      ?.filter((s) => s.key !== "logs-api")
      .map((s) => ({
        value: s.key,
        label: s.key,
        count: s.doc_count,
      })) || []),
  ];

  const statusOptions: SelectOption[] = [
    { value: "", label: "Tous statuts", dividerAfter: true },
    { value: "success", label: "Succès" },
    { value: "error", label: "Erreur" },
  ];

  const ipOptions: SelectOption[] = [
    { value: "", label: "Toutes IPs", dividerAfter: true },
    // Extract unique IPs from recent logs
    ...Array.from(
      new Set(
        recentLogs
          .map((log) => log.ip)
          .filter((ip): ip is string => Boolean(ip)),
      ),
    )
      .slice(0, 10)
      .map((ip) => ({ value: ip, label: ip })),
  ];

  const sourceOptions: SelectOption[] = [
    { value: "", label: "Toutes sources", dividerAfter: true },
    // Extract unique sources from recent logs
    ...Array.from(
      new Set(
        recentLogs
          .map((log) => log.source)
          .filter((source): source is string => Boolean(source)),
      ),
    )
      .slice(0, 10)
      .map((source) => ({ value: source, label: source })),
  ];

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Determine date range based on filter mode
      let startDate: Date;
      let endDate: Date;

      if (dateFilterMode === "month" && selectedMonth) {
        // Month filter: YYYY-MM format
        const [year, month] = selectedMonth.split("-").map(Number);
        startDate = new Date(year, month - 1, 1);
        endDate = new Date(year, month, 0, 23, 59, 59);
      } else if (
        dateFilterMode === "range" &&
        customStartDate &&
        customEndDate
      ) {
        // Custom date range
        startDate = new Date(customStartDate);
        endDate = new Date(customEndDate);
        endDate.setHours(23, 59, 59);
      } else {
        // Default: use timeRange
        endDate = new Date();
        startDate = new Date();
        switch (timeRange) {
          case "1h":
            startDate.setHours(startDate.getHours() - 1);
            break;
          case "6h":
            startDate.setHours(startDate.getHours() - 6);
            break;
          case "24h":
            startDate.setDate(startDate.getDate() - 1);
            break;
          case "7d":
            startDate.setDate(startDate.getDate() - 7);
            break;
        }
      }

      // Chart: full year with monthly interval
      const yearStart = new Date(chartYear, 0, 1).toISOString();
      const yearEnd = new Date(chartYear, 11, 31, 23, 59, 59).toISOString();

      const [statsRes, tlRes, logsRes] = await Promise.all([
        logsApi.getStatsOverview(
          startDate.toISOString(),
          endDate.toISOString(),
          ipFilter || undefined,
          sourceFilter || undefined,
        ),
        logsApi.getTimeline(
          "1M",
          yearStart,
          yearEnd,
          ipFilter || undefined,
          sourceFilter || undefined,
        ),
        logsApi.getLogs({
          page: 1,
          limit: 6,
          sort: "desc",
          excludeService: "logs-api",
          ip: ipFilter || undefined,
          source: sourceFilter || undefined,
        }),
      ]);

      if (statsRes.success) setStats(statsRes.data);
      if (tlRes.success) setTimeline(tlRes.data);
      if (logsRes.success && logsRes.data.logs)
        setRecentLogs(logsRes.data.logs);
    } catch (err) {
      console.error("Dashboard load error:", err);
    } finally {
      setLoading(false);
    }
  }, [
    timeRange,
    ipFilter,
    sourceFilter,
    chartYear,
    dateFilterMode,
    customStartDate,
    customEndDate,
    selectedMonth,
  ]);

  useEffect(() => {
    loadData();
    const id = setInterval(loadData, 30000);
    return () => clearInterval(id);
  }, [loadData]);

  // Derived data
  const errorRate =
    stats && stats.total > 0
      ? ((stats.errorCount / stats.total) * 100).toFixed(1)
      : "0";
  const successCount = stats ? stats.total - stats.errorCount : 0;

  // Multi-line chart data broken down by level + service — monthly for the selected year
  const monthNames = [
    "Jan",
    "Fév",
    "Mar",
    "Avr",
    "Mai",
    "Juin",
    "Juil",
    "Aoû",
    "Sep",
    "Oct",
    "Nov",
    "Déc",
  ];

  // Extract unique service names from timeline (exclude logs-api)
  const serviceNames = Array.from(
    new Set(
      timeline.flatMap((b) =>
        (b.by_service?.buckets || [])
          .map((s) => s.key)
          .filter((k) => k !== "logs-api"),
      ),
    ),
  ).sort();

  // Build a full 12-month skeleton, then fill with timeline data
  const chartData = monthNames.map((mName, idx) => {
    const bucket = timeline.find((b) => {
      const d = new Date(b.key_as_string);
      return d.getMonth() === idx && d.getFullYear() === chartYear;
    });
    const levelBuckets = bucket?.by_level?.buckets || [];
    const serviceBuckets = bucket?.by_service?.buckets || [];
    const row: Record<string, string | number> = {
      name: mName,
      total: bucket?.doc_count || 0,
      info: levelBuckets.find((l) => l.key === "info")?.doc_count || 0,
      warn: levelBuckets.find((l) => l.key === "warn")?.doc_count || 0,
      error: levelBuckets.find((l) => l.key === "error")?.doc_count || 0,
      debug: levelBuckets.find((l) => l.key === "debug")?.doc_count || 0,
    };
    // Add each service count
    serviceNames.forEach((svc) => {
      row[svc] = serviceBuckets.find((s) => s.key === svc)?.doc_count || 0;
    });
    return row;
  });

  // Colors palette for service lines
  const serviceColors = [
    "#10b981",
    "#f59e0b",
    "#3b82f6",
    "#ec4899",
    "#14b8a6",
    "#8b5cf6",
    "#f43f5e",
    "#0ea5e9",
    "#d946ef",
    "#84cc16",
  ];

  // Donut data
  const donutData = stats?.byLevel || [];
  const totalCalls = stats?.total || 0;

  // Filtered logs for the mini table
  const filteredLogs = recentLogs.filter((log) => {
    if (levelFilter && log.level !== levelFilter) return false;
    if (serviceFilter && log.service !== serviceFilter) return false;
    if (ipFilter && log.ip !== ipFilter) return false;
    if (
      logSearch &&
      !log.message.toLowerCase().includes(logSearch.toLowerCase()) &&
      !log.service.toLowerCase().includes(logSearch.toLowerCase())
    )
      return false;
    if (statusFilter) {
      const isError = log.level === "error";
      if (statusFilter === "success" && isError) return false;
      if (statusFilter === "error" && !isError) return false;
    }
    return true;
  });

  return (
    <div>
      {/* Header with filters */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2
            className="text-[22px] font-bold"
            style={{ color: t.text.primary }}
          >
            Bonjour, {user?.name || "Utilisateur"}
          </h2>
          <p className="text-[13px] mt-0.5" style={{ color: t.text.secondary }}>
            Vue d'ensemble de l'activité API
          </p>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowDateFilter(!showDateFilter)}
            className="flex items-center gap-2 px-3 py-2 rounded-lg transition-all hover:shadow-sm"
            style={{
              backgroundColor: showDateFilter ? t.primaryLight : t.surface,
              border: `1px solid ${showDateFilter ? t.primary : t.border}`,
              color: showDateFilter ? t.primary : t.text.secondary,
            }}
          >
            <Calendar className="w-4 h-4" />
            <span className="text-[12px] font-medium">Filtrer par date</span>
          </button>

          <div className="flex items-center gap-2">
            <span
              className="text-[12px] font-medium"
              style={{ color: t.text.secondary }}
            >
              IP:
            </span>
            <CustomSelect
              value={ipFilter}
              onChange={setIpFilter}
              placeholder="Toutes IPs"
              icon={<Globe className="w-3.5 h-3.5" />}
              size="sm"
              className="w-[140px]"
              options={ipOptions}
            />
          </div>
          <div className="flex items-center gap-2">
            <span
              className="text-[12px] font-medium"
              style={{ color: t.text.secondary }}
            >
              Source:
            </span>
            <CustomSelect
              value={sourceFilter}
              onChange={setSourceFilter}
              placeholder="Toutes sources"
              icon={<Activity className="w-3.5 h-3.5" />}
              size="sm"
              className="w-[140px]"
              options={sourceOptions}
            />
          </div>
        </div>
      </div>

      {/* Date Filter Panel */}
      {showDateFilter && (
        <div
          className="rounded-xl p-5 mb-6 animate-fade-in"
          style={{
            backgroundColor: t.surface,
            border: `1px solid ${t.border}`,
          }}
        >
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-4 h-4" style={{ color: t.primary }} />
            <h3
              className="text-[14px] font-semibold"
              style={{ color: t.text.primary }}
            >
              Filtres de Date
            </h3>
          </div>

          {/* Filter Mode Toggle */}
          <div className="flex items-center gap-3 mb-4">
            <button
              onClick={() => {
                setDateFilterMode("range");
                setSelectedMonth("");
              }}
              className="px-4 py-2 rounded-lg text-[12px] font-medium transition-all"
              style={{
                backgroundColor:
                  dateFilterMode === "range" ? t.primary : t.surface,
                color: dateFilterMode === "range" ? "#fff" : t.text.secondary,
                border: `1px solid ${dateFilterMode === "range" ? t.primary : t.border}`,
              }}
            >
              Période personnalisée
            </button>
            <button
              onClick={() => {
                setDateFilterMode("month");
                setCustomStartDate("");
                setCustomEndDate("");
              }}
              className="px-4 py-2 rounded-lg text-[12px] font-medium transition-all"
              style={{
                backgroundColor:
                  dateFilterMode === "month" ? t.primary : t.surface,
                color: dateFilterMode === "month" ? "#fff" : t.text.secondary,
                border: `1px solid ${dateFilterMode === "month" ? t.primary : t.border}`,
              }}
            >
              Par mois
            </button>
            {(customStartDate || customEndDate || selectedMonth) && (
              <button
                onClick={() => {
                  setCustomStartDate("");
                  setCustomEndDate("");
                  setSelectedMonth("");
                }}
                className="px-3 py-2 rounded-lg text-[12px] font-medium transition-all hover:bg-red-50"
                style={{
                  backgroundColor: t.surface,
                  color: t.danger,
                  border: `1px solid ${t.border}`,
                }}
              >
                Réinitialiser
              </button>
            )}
          </div>

          {/* Date Range Inputs */}
          {dateFilterMode === "range" && (
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <label
                  className="text-[12px] font-medium"
                  style={{ color: t.text.secondary }}
                >
                  Du
                </label>
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="px-3 py-2 rounded-lg text-[12px]"
                  style={{
                    border: `1px solid ${t.border}`,
                    color: t.text.primary,
                  }}
                />
              </div>
              <div className="flex items-center gap-2">
                <label
                  className="text-[12px] font-medium"
                  style={{ color: t.text.secondary }}
                >
                  Au
                </label>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="px-3 py-2 rounded-lg text-[12px]"
                  style={{
                    border: `1px solid ${t.border}`,
                    color: t.text.primary,
                  }}
                />
              </div>
              {customStartDate && customEndDate && (
                <div
                  className="text-[12px] px-3 py-2 rounded-lg"
                  style={{ backgroundColor: t.primaryLight, color: t.primary }}
                >
                  {Math.ceil(
                    (new Date(customEndDate).getTime() -
                      new Date(customStartDate).getTime()) /
                      (1000 * 60 * 60 * 24),
                  )}{" "}
                  jours
                </div>
              )}
            </div>
          )}

          {/* Month Selector */}
          {dateFilterMode === "month" && (
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <label
                  className="text-[12px] font-medium"
                  style={{ color: t.text.secondary }}
                >
                  Mois
                </label>
                <input
                  type="month"
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  className="px-3 py-2 rounded-lg text-[12px]"
                  style={{
                    border: `1px solid ${t.border}`,
                    color: t.text.primary,
                  }}
                />
              </div>
              {selectedMonth && (
                <div
                  className="text-[12px] px-3 py-2 rounded-lg"
                  style={{ backgroundColor: t.primaryLight, color: t.primary }}
                >
                  {new Date(selectedMonth + "-01").toLocaleDateString("fr-FR", {
                    month: "long",
                    year: "numeric",
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Row 1: Metrics (2×2) + Bar Chart ── */}
      {loading ? (
        <div className="grid grid-cols-12 gap-5 mb-5">
          <div className="col-span-12 lg:col-span-5 grid grid-cols-2 gap-4">
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </div>
          <div className="col-span-12 lg:col-span-7">
            <SkeletonChart />
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-12 gap-5 mb-5 animate-fade-in">
          {/* Services list */}
          <div
            className="col-span-12 lg:col-span-5 rounded-2xl p-5"
            style={{
              backgroundColor: t.surface,
              border: `1px solid ${t.border}`,
            }}
          >
            <h3
              className="text-[15px] font-bold mb-4"
              style={{ color: t.text.primary }}
            >
              Services
            </h3>
            <div className="space-y-2">
              {stats?.byService
                ?.filter((s) => s.key !== "logs-api")
                .map((service, index) => (
                  <div
                    key={service.key}
                    className="flex items-center justify-between p-3 rounded-xl transition-all hover:shadow-sm"
                    style={{ backgroundColor: t.primaryLight }}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold text-white"
                        style={{
                          backgroundColor:
                            serviceColors[index % serviceColors.length],
                        }}
                      >
                        {service.key.charAt(0).toUpperCase()}
                      </div>
                      <span
                        className="text-[13px] font-medium"
                        style={{ color: t.text.primary }}
                      >
                        {service.key}
                      </span>
                    </div>
                    <span
                      className="text-[13px] font-bold"
                      style={{ color: t.primary }}
                    >
                      {service.doc_count.toLocaleString()}
                    </span>
                  </div>
                ))}
              {stats?.byService?.filter((s) => s.key !== "logs-api").length ===
                0 && (
                <div
                  className="text-center py-8 text-[13px]"
                  style={{ color: t.text.muted }}
                >
                  Aucun service trouvé
                </div>
              )}
            </div>
          </div>

          {/* Bar chart */}
          <div
            className="col-span-12 lg:col-span-7 rounded-2xl p-5"
            style={{
              backgroundColor: t.surface,
              border: `1px solid ${t.border}`,
            }}
          >
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3
                  className="text-[15px] font-bold"
                  style={{ color: t.text.primary }}
                >
                  Performance API
                </h3>
                <p
                  className="text-[11px] mt-0.5"
                  style={{ color: t.text.muted }}
                >
                  Nombre de logs par niveau
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setChartYear((y) => y - 1)}
                  className="p-1.5 rounded-lg transition-colors hover:bg-[#f3f0ff]"
                  style={{
                    border: `1px solid ${t.border}`,
                    background: "transparent",
                  }}
                  title="Année précédente"
                >
                  <ChevronLeft
                    className="w-4 h-4"
                    style={{ color: t.text.secondary }}
                  />
                </button>
                <span
                  className="text-[13px] font-semibold min-w-[50px] text-center"
                  style={{ color: t.text.primary }}
                >
                  {chartYear}
                </span>
                <button
                  onClick={() => setChartYear((y) => y + 1)}
                  className="p-1.5 rounded-lg transition-colors hover:bg-[#f3f0ff]"
                  style={{
                    border: `1px solid ${t.border}`,
                    background: "transparent",
                  }}
                  disabled={chartYear >= new Date().getFullYear()}
                  title="Année suivante"
                >
                  <ChevronRight
                    className="w-4 h-4"
                    style={{
                      color:
                        chartYear >= new Date().getFullYear()
                          ? t.border
                          : t.text.secondary,
                    }}
                  />
                </button>
              </div>
            </div>

            {chartData.length === 0 ? (
              <div className="h-[220px] flex items-center justify-center">
                <p className="text-[13px]" style={{ color: t.text.muted }}>
                  Aucune donnée disponible pour cette période
                </p>
              </div>
            ) : (
              <div style={{ width: "100%", height: 220 }}>
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart
                    data={chartData}
                    margin={{ top: 5, right: 10, left: 0, bottom: 5 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="#f3f0ff"
                      vertical={false}
                    />
                    <XAxis
                      dataKey="name"
                      fontSize={11}
                      stroke={t.text.muted}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      fontSize={11}
                      stroke={t.text.muted}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: t.surface,
                        border: `1px solid ${t.border}`,
                        borderRadius: "12px",
                        fontSize: "12px",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                      }}
                      itemStyle={{ fontSize: "12px" }}
                    />
                    <Line
                      type="monotone"
                      dataKey="total"
                      name="Total Appels"
                      stroke="#7c3aed"
                      strokeWidth={2.5}
                      dot={{ r: 3, fill: "#7c3aed", strokeWidth: 0 }}
                      activeDot={{ r: 5, strokeWidth: 2, stroke: "#fff" }}
                      animationDuration={1200}
                      animationEasing="ease-in-out"
                    />
                    <Line
                      type="monotone"
                      dataKey="info"
                      name="Info"
                      stroke="#06b6d4"
                      strokeWidth={2.5}
                      dot={{ r: 3, fill: "#06b6d4", strokeWidth: 0 }}
                      activeDot={{ r: 5, strokeWidth: 2, stroke: "#fff" }}
                      animationDuration={1200}
                      animationEasing="ease-in-out"
                    />
                    <Line
                      type="monotone"
                      dataKey="error"
                      name="Erreurs"
                      stroke="#ef4444"
                      strokeWidth={2.5}
                      dot={{ r: 3, fill: "#ef4444", strokeWidth: 0 }}
                      activeDot={{ r: 5, strokeWidth: 2, stroke: "#fff" }}
                      animationDuration={1200}
                      animationEasing="ease-in-out"
                    />
                    <Line
                      type="monotone"
                      dataKey="warn"
                      name="Warnings"
                      stroke="#f97316"
                      strokeWidth={2.5}
                      dot={{ r: 3, fill: "#f97316", strokeWidth: 0 }}
                      activeDot={{ r: 5, strokeWidth: 2, stroke: "#fff" }}
                      animationDuration={1200}
                      animationEasing="ease-in-out"
                    />
                    <Line
                      type="monotone"
                      dataKey="debug"
                      name="Debug"
                      stroke="#a78bfa"
                      strokeWidth={2.5}
                      dot={{ r: 3, fill: "#a78bfa", strokeWidth: 0 }}
                      activeDot={{ r: 5, strokeWidth: 2, stroke: "#fff" }}
                      animationDuration={1200}
                      animationEasing="ease-in-out"
                    />
                    {serviceNames.map((svc, i) => (
                      <Line
                        key={svc}
                        type="monotone"
                        dataKey={svc}
                        name={svc}
                        stroke={serviceColors[i % serviceColors.length]}
                        strokeWidth={2}
                        strokeDasharray="5 3"
                        dot={{
                          r: 2,
                          fill: serviceColors[i % serviceColors.length],
                          strokeWidth: 0,
                        }}
                        activeDot={{ r: 4, strokeWidth: 2, stroke: "#fff" }}
                        animationDuration={1400}
                        animationEasing="ease-in-out"
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Row 2: Logs Table + Donut ── */}
      {loading ? (
        <div className="grid grid-cols-12 gap-5">
          <div className="col-span-12 lg:col-span-8">
            <SkeletonTable rows={5} />
          </div>
          <div className="col-span-12 lg:col-span-4">
            <SkeletonDonut />
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-12 gap-5 animate-fade-in">
          {/* Logs Table */}
          <div
            className="col-span-12 rounded-2xl p-5"
            style={{
              backgroundColor: t.surface,
              border: `1px solid ${t.border}`,
            }}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <h3
                className="text-[15px] font-bold"
                style={{ color: t.text.primary }}
              >
                Appels API Récents
              </h3>
              {/* <div className="relative w-52 h-8 flex items-center">
                <Search className="absolute left-3 w-3.5 h-3.5 pointer-events-none" style={{ color: t.text.muted }} />
                <input
                  type="text"
                  value={logSearch}
                  onChange={(e) => setLogSearch(e.target.value)}
                  placeholder="Rechercher appels..."
                  className="w-full h-full pl-8 pr-3 rounded-lg text-[12px]"
                  style={{ backgroundColor: '#faf9fc', border: `1px solid ${t.border}`, color: t.text.primary, outline: 'none', lineHeight: '1.5' }}
                />
              </div> */}
            </div>

            {/* Table */}
            <table className="w-full">
              <thead>
                <tr style={{ borderBottom: `1px solid ${t.border}` }}>
                  <th
                    className="text-left py-2.5 px-2 text-[11px] font-semibold uppercase tracking-wide"
                    style={{ color: t.text.muted }}
                  >
                    Service
                  </th>
                  <th
                    className="text-left py-2.5 px-2 text-[11px] font-semibold uppercase tracking-wide"
                    style={{ color: t.text.muted }}
                  >
                    Niveau
                  </th>
                  <th
                    className="text-left py-2.5 px-2 text-[11px] font-semibold uppercase tracking-wide"
                    style={{ color: t.text.muted }}
                  >
                    Message
                  </th>
                  <th
                    className="text-left py-2.5 px-2 text-[11px] font-semibold uppercase tracking-wide"
                    style={{ color: t.text.muted }}
                  >
                    Endpoint
                  </th>
                  <th
                    className="text-left py-2.5 px-2 text-[11px] font-semibold uppercase tracking-wide"
                    style={{ color: t.text.muted }}
                  >
                    Date
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredLogs.slice(0, 5).map((log, i) => (
                  <tr
                    key={log.id || i}
                    className="group transition-colors duration-150 hover:bg-[#faf8ff]"
                    style={{ borderBottom: `1px solid ${t.border}` }}
                  >
                    <td
                      className="py-2.5 px-2 text-[12px] font-medium"
                      style={{ color: t.text.primary }}
                    >
                      {log.service}
                    </td>
                    <td className="py-2.5 px-2">
                      <span
                        className="text-[10px] px-2 py-0.5 rounded-full font-semibold"
                        style={{
                          backgroundColor:
                            log.level === "error"
                              ? t.dangerBg
                              : log.level === "warn"
                                ? t.warnBg
                                : t.primaryLight,
                          color:
                            log.level === "error"
                              ? t.danger
                              : log.level === "warn"
                                ? t.warn
                                : t.primary,
                        }}
                      >
                        {log.level}
                      </span>
                    </td>
                    <td
                      className="py-2.5 px-2 text-[12px] max-w-[200px] truncate"
                      style={{ color: t.text.secondary }}
                    >
                      {log.message}
                    </td>
                    <td
                      className="py-2.5 px-2 text-[12px]"
                      style={{ color: t.text.secondary }}
                    >
                      {log.path || "—"}
                    </td>
                    <td
                      className="py-2.5 px-2 text-[11px]"
                      style={{ color: t.text.muted }}
                    >
                      {new Date(log.timestamp).toLocaleDateString("fr-FR", {
                        day: "2-digit",
                        month: "short",
                      })}
                    </td>
                  </tr>
                ))}
                {filteredLogs.length === 0 && (
                  <tr>
                    <td
                      colSpan={5}
                      className="py-8 text-center text-[13px]"
                      style={{ color: t.text.muted }}
                    >
                      Aucun appel API trouvé
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
